<template>
  <div id="import-dialog-backdrop" class="modal fade" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-md modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-body p-5">
          <div class="import-dialog">
            <header>
              <h3><strong>{{ $t('import_recording.dialog.import_tracks') }}</strong></h3>
              <i18n-t keypath="import_recording.dialog.these_must_be_recordings" tag="p" class="import-note text-muted small mb-3" scope="global">
                <template #recordings><strong>{{ $t('import_recording.dialog.recordings') }}</strong></template>
              </i18n-t>
            </header>
            <div class="file-drop-area rounded-lg p-3 d-flex flex-column align-items-center"
                 @drop.prevent="handleFileDrop"
                 @dragover.prevent>
              <p class="mb-3">{{ $t('import_recording.dialog.drag_drop_gpx_or_fit_files') }}</p>
              <input ref="fileInput"
                     type="file"
                     @change="handleFileChange"
                     multiple
                     accept=".gpx,.fit"
                     hidden>
              <button class="browse-button btn btn-sm btn-secondary" @click="triggerFileInput">{{ $t('shared.choose_files') }}</button>
            </div>
            <ul class="file-list mt-3">
              <li v-for="(file, index) in files" :key="index" class="file-item">
                <span class="file-name">{{ file.name }}</span>
                <button class="remove-btn" @click="removeFile(index)" :title="$t('shared.remove')">&times;</button>
              </li>
            </ul>
            <!-- Single Import Button -->
            <div class="w-100 d-flex justify-content-center mt-3">
              <button class="btn btn-sm btn-primary" v-if="files.length > 0 && !isImporting"
                      @click="importAllFiles">
                {{ $t('import_recording.dialog.import_files') }}
              </button>
              <div v-else-if="isImporting" class="loading">{{ $t('import_recording.dialog.importing') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import GpxParser from "gpxparser";
import FitParser from 'fit-file-parser';
import graph_client from "../../utils/graphql/graph_client";
import { CreateRecording } from "../../utils/graphql/recorded-trail-graph";
import { buildImportRecordingInput } from "./importRecordingInput";
import { GpxImportError, GpxImportErrorCode } from "../../utils/helpers/gpxImportError";
import { gpxTextImportError, gpxTrackImportError, hasTracks, toTrackPoints } from "./gpxTrackImport";
import { gpxImportErrorMessage } from "../../utils/helpers/gpxImportErrorMessage";
import { Modal } from "bootstrap";

// gpxparser's queryDirectSelector uses undeclared `idx`/`elem` globals, which
// ReferenceError under strict mode (ESM). It's only hit when a parent element
// has >1 descendants matching a tag — e.g. metadata containing both
// <author><link/></author> and a top-level <link/> (common in Strava exports).
if (!(GpxParser as any).__queryDirectSelectorPatched) {
  (GpxParser as any).prototype.queryDirectSelector = function (node: Element, tagName: string) {
    const nodes = node.querySelectorAll(tagName);
    let trueNode: Element | undefined = nodes[0];
    if (nodes.length > 1) {
      const children = node.childNodes;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as Element;
        if (child && child.tagName === tagName) {
          trueNode = child;
        }
      }
    }
    return trueNode;
  };
  (GpxParser as any).__queryDirectSelectorPatched = true;
}

export default {
  data() {
    return {
      files: [],
      isImporting: false, // Track if importing is in progress
    };
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    handleFileChange(event) {
      const filesAdded = Array.from(event.target.files);
      this.files.push(...filesAdded.map(file => ({
        name: file.name,
        file: file,
      })));
    },
    handleFileDrop(event) {
      const filesAdded = Array.from(event.dataTransfer.files);
      this.files.push(...filesAdded.map(file => ({
        name: file.name,
        file: file,
      })));
    },
    async importAllFiles() {
      if (this.isImporting) return;
      this.isImporting = true; // Start importing

      const imported = [];
      try {
        // Import each file sequentially
        for (const fileEntry of this.files) {
          const recording = await this.importFile(fileEntry);
          if (recording) imported.push(recording);
        }

        // Clear files and reset input after successful import
        this.files = [];
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }

        // Only the recordings page defines this; the peaks page listens for the
        // event below instead.
        window.reloadRecordedTrails?.();
        window.dispatchEvent(new CustomEvent('hiiker:recordings-imported', {
          detail: { recordings: imported },
        }));
        const element = document.getElementById('import-dialog-backdrop');
        const modal = Modal.getInstance(element);
        modal.hide();

        if (imported.length > 0) {
          this.showImportedToast(imported);
        }
      } catch (error) {
        console.error('Import error:', error);
        if (error instanceof GpxImportError) {
          alert(gpxImportErrorMessage(error));
        } else {
          alert(this.$t('import_recording.dialog.failed_to_import_files', { error: error.message }));
        }
      } finally {
        this.isImporting = false; // Reset importing state
      }
    },
    showImportedToast(recordings) {
      const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      }[c]));
      const recordingUrl = (r) => r.shareableUrl || `/user/recordings/${r.id}`;

      const header = recordings.length === 1
        ? this.$t('import_recording.dialog.recording_imported')
        : this.$t('import_recording.dialog.count_recordings_imported', { count: recordings.length });
      const itemsHtml = recordings.map((r) => {
        const label = escapeHtml(r.title || this.$t('import_recording.dialog.untitled_recording'));
        return `<li><a href="${recordingUrl(r)}" class="text-white text-decoration-underline">${label}</a></li>`;
      }).join('');

      const toastHtml = `
        <div class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 1080;">
          <div class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
            <div class="toast-header bg-gray text-white">
              <strong class="me-auto">${escapeHtml(header)}</strong>
              <button type="button" class="btn-close btn-close-white" aria-label="${escapeHtml(this.$t('shared.close'))}"></button>
            </div>
            <div class="toast-body bg-dark text-white">
              <ul class="mb-0 ps-3">${itemsHtml}</ul>
            </div>
          </div>
        </div>
      `;

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = toastHtml.trim();
      const toastElement = tempDiv.firstElementChild as HTMLElement | null;
      if (!toastElement) return;

      document.body.appendChild(toastElement);
      const closeBtn = toastElement.querySelector('.btn-close');
      closeBtn?.addEventListener('click', () => toastElement.remove());
      // Auto-dismiss after 15s
      setTimeout(() => toastElement.remove(), 15000);
    },
    async importFile(fileEntry) {
      try {
        const file = fileEntry.file;
        const title = file.name.replace(/\.[^/.]+$/, '');
        const extension = file.name.split('.').pop().toLowerCase();
        let trackData;
        if (extension === 'gpx') {
          trackData = await this.parseGpxFile(file, title);
        } else if (extension === 'fit') {
          trackData = await this.parseFitFile(file, title);
        } else {
          throw new Error(this.$t('import_recording.dialog.unsupported_file_type'));
        }

        // Stamped `source: IMPORTED` so the recording overview can label it as an import.
        const variables = {
          input: buildImportRecordingInput(trackData, `upload-${Date.now()}`, new Date().toISOString())
        };

        const response: any = await graph_client.request(CreateRecording, variables);

        if (response?.createRecording?.errors?.length > 0) {
          const errs = response.createRecording.errors
            .map((e: any) => `${e.fieldName}: ${e.errors.join(', ')}`)
            .join('\n');
          throw new Error(`${this.$t('import_recording.dialog.errors_creating_recording')}\n${errs}`);
        }
        if (!response?.createRecording?.id) {
          throw new Error(this.$t('import_recording.dialog.unknown_error_creating_recording'));
        }

        return {
          id: response.createRecording.id,
          title: response.createRecording.title,
          shareableUrl: response.createRecording.shareableUrl,
        };
      } catch (error) {
        console.error('Import error:', error);
        throw error; // Re-throw the error to handle it in importAllFiles
      }
    },
    async parseGpxFile(file, title) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const text = e.target.result as string;
            const textError = gpxTextImportError(text);
            if (textError) {
              throw new GpxImportError(textError);
            }

            let parsed = new GpxParser();
            parsed.parse(text);

            if (!hasTracks(parsed.tracks)) {
              throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack);
            }

            const points = toTrackPoints(parsed.tracks[0].points);
            const time = points[0]?.time || null;
            // Why a route without per-point times is refused rather than
            // imported: see gpxTrackImportError.
            const trackError = gpxTrackImportError(points);
            if (trackError) {
              throw new GpxImportError(trackError);
            }
            if (parsed.metadata && parsed.metadata.name) {
              title = parsed.metadata.name;
            }
            title = this.stripEmoji(title);
            resolve(this.processTrackData(points, time, title));
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new GpxImportError(GpxImportErrorCode.Unreadable));
        reader.readAsText(file);
      });
    },
    async parseFitFile(file, title) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            new FitParser({
              force: true,
              speedUnit: 'm/s',
            }).parse(e.target.result, (error, data) => {
              if (error) return reject(error);
              const points = data.records
                .filter(r => r.position_lat && r.position_long)
                .map(r => ({
                  lat: r.position_lat,
                  lon: r.position_long,
                  elevation: r.altitude,
                  time: r.timestamp,
                  speed: r.speed,
                  course: r.enhanced_speed || r.speed,
                  hAccuracy: r.h_accuracy,
                  vAccuracy: r.v_accuracy,
                }));
              const createdAt = points[points.length - 1].time;
              resolve(this.processTrackData(points, createdAt, title));
            });
          } catch (error) {
            reject(error);
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },
    processTrackData(points, createdAt, title) {
      let cumulativeDistance = 0;
      let previousPoint = null;
      const hasTimestamps = points.length > 0 && points[0].time != null;
      const result = {
        lineString: 'LINESTRING (',
        altitudes: [],
        distances: [],
        timeStamps: [],
        speeds: [],
        courses: [],
        hAccuracies: [],
        vAccuracies: [],
        createdAt: createdAt || new Date(),
        title: title,
      };

      points.forEach((p, i) => {
        if (previousPoint) {
          cumulativeDistance += this.calculateDistance(previousPoint, p);
        }
        previousPoint = p;

        result.lineString += `${p.lon} ${p.lat} ${p.elevation || 0} ${cumulativeDistance}`;
        if (i < points.length - 1) result.lineString += ', ';

        result.altitudes.push(p.elevation || 0);
        result.distances.push(cumulativeDistance);
        if (hasTimestamps) {
          result.timeStamps.push(new Date(p.time).getTime());
        }
        result.speeds.push(p.speed || 0);
        result.courses.push(p.course || 0);
        result.hAccuracies.push(p.hAccuracy || 0);
        result.vAccuracies.push(p.vAccuracy || 0);
      });

      result.lineString += ')';
      result.elapsedSeconds = result.timeStamps.length > 1
        ? parseInt((result.timeStamps[result.timeStamps.length - 1] - result.timeStamps[0]) / 1000)
        : 0;

      return result;
    },
    stripEmoji(str) {
      if (!str) return str;
      return str
        .replace(/\p{Extended_Pictographic}/gu, '')
        .replace(/\s+/g, ' ')
        .trim();
    },
    calculateDistance(a, b) {
      const R = 6371e3;
      const φ1 = a.lat * Math.PI / 180;
      const φ2 = b.lat * Math.PI / 180;
      const Δφ = (b.lat - a.lat) * Math.PI / 180;
      const Δλ = (b.lon - a.lon) * Math.PI / 180;

      const haversine = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
        Math.cos(φ1) * Math.cos(φ2) *
        Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

      return R * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    },
  },
};
</script>

<style scoped>

.file-drop-area {
  border: 2px dashed var(--gray-border);
  padding: 10px;
  text-align: center;
  margin: 20px 0;
}

.file-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  margin-bottom: 6px;
  gap: 10px;
}

.file-name {
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.remove-btn {
  background: none;
  border: none;
  color: var(--bs-secondary, #6c757d);
  font-size: 1.2rem;
  line-height: 1;
  padding: 0 2px;
  flex-shrink: 0;
  opacity: 0.6;
}

.remove-btn:hover {
  opacity: 1;
  color: var(--bs-danger, #dc3545);
}

button {
  cursor: pointer;
}

.loading {
  color: #555;
}
</style>
